/**
 * Image URL utilities for Core Frontend
 */

import { getS3ProductsUrl, getS3LogosUrl } from '@/providers/PlatformBootstrapProvider';

/**
 * Get full product image URL from relative path
 *
 * Handles both relative paths (from Catalog API) and absolute URLs (legacy)
 *
 * @param relativePath - Relative path like "products/{modelGuid}/0.webp" or absolute URL
 * @returns Full URL to product image
 *
 * @example
 * getProductImageUrl("products/abc-123/0.webp")
 * // Returns: "https://s3.teletienda.ar/teletienda-products/products/abc-123/0.webp"
 *
 * getProductImageUrl("https://cdn.example.com/image.jpg")
 * // Returns: "https://cdn.example.com/image.jpg" (unchanged)
 */
export function getProductImageUrl(relativePath: string | null | undefined): string | undefined {
  if (!relativePath) {
    return undefined;
  }

  // If already an absolute URL, return as-is
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }

  // Build full URL from relative path
  return `${getS3ProductsUrl()}/${relativePath}`;
}

/**
 * Build the deterministic merchant logo URL from a merchantGuid.
 *
 * Logo URL pattern: https://s3.teletienda.ar/merchant-logos/{merchantGuid}/logo.webp
 *
 * ⚠️ MPF-4: this GUESSES the S3 path — only call it when the logo is known to exist.
 * For merchant cards prefer {@link resolveMerchantCardLogo}, which never guesses and
 * falls back to the Avatar's initials when the backend left `logo_url` null (a guessed
 * URL for a logoless merchant produces a failed/NS_BINDING_ABORTED request).
 *
 * @param merchantGuid - Merchant UUID
 * @returns Full URL to merchant logo
 */
export function getMerchantLogoUrl(merchantGuid: string): string {
  return `${getS3LogosUrl()}/${merchantGuid}/logo.webp`;
}

/**
 * Resolve the logo URL to render on a merchant card, honoring dark mode.
 *
 * MPF-4: returns ONLY a logo the backend actually persisted (`logo_url` /
 * `logo_url_dark`). When none exists it returns `undefined` — the card's Avatar then
 * renders the business-name initial instead of requesting a non-existent S3 object.
 *
 * @param merchant - the card's merchant (logoUrl / logoUrlDark may be null/undefined)
 * @param isDark - whether the computed color scheme is dark
 * @returns a real logo URL, or `undefined` to fall back to initials
 */
export function resolveMerchantCardLogo(
  merchant: { logoUrl?: string; logoUrlDark?: string | null },
  isDark: boolean,
): string | undefined {
  if (isDark && merchant.logoUrlDark) return merchant.logoUrlDark;
  return merchant.logoUrl || undefined;
}

/**
 * Resolve a build-time public asset (a file under `public/`, e.g. `images/...`)
 * to a URL that respects Vite's configured `base`.
 *
 * Under SFU (epic D3/D7) the built app's `base` is the static-asset host
 * (`https://static-dev.teletienda.ar/core-frontend/`), so public files are
 * served from S3 — not the app origin. A hardcoded root-absolute string like
 * `'/images/logo.png'` is a runtime literal that Vite's `base` rewrite never
 * touches, so it 404s against the app origin. Prefixing with
 * `import.meta.env.BASE_URL` mirrors what Vite already does to `index.html`
 * references (favicon, JS chunks).
 *
 * In local `npm run dev`, `BASE_URL` is `'/'`, so this returns `/images/...`
 * (served by the Vite dev server from `public/`).
 *
 * @param path - Path relative to `public/`, with or without a leading slash.
 * @returns Base-aware absolute URL to the public asset.
 *
 * @example
 * publicAsset('images/logo-white.png')
 * // built: 'https://static-dev.teletienda.ar/core-frontend/images/logo-white.png'
 * // dev:   '/images/logo-white.png'
 */
export function publicAsset(path: string): string {
  // BASE_URL always ends with '/'; strip any leading slash to avoid '//'.
  return `${import.meta.env.BASE_URL}${path.replace(/^\/+/, '')}`;
}
